import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Button,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import dataService from "../../services/dataService";
import type {
  CartResponse,
  CartItem,
  CouponCode
} from "../../models/cart/types";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ProductListEnumType from "@/enums/productListEnumType";
import { useGlobalNotification } from "@/app/components/header/GlobalNotificationContext";
import ItemSourceEnum from "@/enums/itemSourceEnum";
import BuilderIOSection from "../../builderio/components/builderIOSection";
import { BuilderIOSections, isEagleViewProduct } from "@/app/models/constants";
import ShareCartDialog from "./shareCartDialog";
import CartCheckoutSummary from "./cartCheckoutSummary";
import { CartItemRow } from "./cartItems";
import CartToProductListSidebar from "./cartToProductListSidebar";
import { LoadingStatusEnum } from "@/enums/loadingStatusEnum";
import { clearFromStorage, loadFromStorage, saveToStorage } from "@/app/utils/localStorageHelper/checkoutStorage";
import { STORAGE_KEYS, EagleViewAddressPrefill } from "@/app/utils/localStorageHelper/checkoutKeys";
import { RootState } from "@/app/store/store";
import { useSelector } from "react-redux";
import { SavedDeliveryPickupPayload } from "@/app/models/checkout/types";

type CartPageProps = {
  setDisableShipTo: (val: boolean) => void;
};

// Page
const CartPage: React.FC<CartPageProps> = ({ setDisableShipTo }) => {
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState<CartResponse | null>(null);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatusEnum>(LoadingStatusEnum.IDLE);

  // RIGHT summary state
  const [fulfillment, setFulfillment] = useState<"pickup" | "delivery">("pickup");
  const [showPromo, setShowPromo] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [couponCodes, setCouponCodes] = useState<CouponCode[]>([]);
  const [couponMessages, setCouponMessages] = useState<string[]>([]);
  const [couponApplied, setCouponApplied] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cartItemForTemplate, setCartItemForTemplate] = useState<CartItem | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const items = cart?.items ?? [];
  const itemsCount = items.length;
  const cartHasEagleView = items.some((item) => isEagleViewProduct(item.sku));
  const orderSubtotal = useMemo(() => cart?.subTotal ?? 0, [cart]);
  const userId = useSelector((state: RootState) => state.userState.user.id)?.toString() ?? '0';
  const eagleViewAddressAvailable = cartHasEagleView
    ? !!loadFromStorage<EagleViewAddressPrefill>(userId, STORAGE_KEYS.EAGLEVIEW_ADDRESS_PREFILL)
    : false;
  const { setNotification, removeNotification } = useGlobalNotification();

  useEffect(() => {
    setDisableShipTo(true);

    return () => {
      setDisableShipTo(false);
    };
  }, [setDisableShipTo]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!cancelled)
          await getCart(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      removeNotification();
    };
  }, []);

  useEffect(() => {
    const cached = loadFromStorage<SavedDeliveryPickupPayload>(
      userId,
      STORAGE_KEYS.PICKUP_DELIVERY
    );

    if (cached && typeof cached.isPickup === "boolean") {
      setFulfillment(cached.isPickup ? "pickup" : "delivery");
    }
  }, [userId]);

  useEffect(() => {
    if (cartHasEagleView) {
      setFulfillment("delivery");
    }
  }, [cartHasEagleView]);

  useEffect(() => {
    if (cartHasEagleView && !eagleViewAddressAvailable) {
      setNotification(
        "Your EagleView product was removed because the property address selection expired. Please add it again and select a property address.",
        "warning",
        true
      );
      handleRemoveAll();
    }
  }, [cart]);

  const getCart = async (handleDiscountErrors: boolean = true) => {
    const response = await dataService.getCart();
    if (response?.success && response.data) {
      setCart(response.data);
      var coupon = response.data.coupon;
      var messages = coupon?.messages && coupon?.messages.length > 0 ? coupon.messages : coupon?.errorMessage ? [coupon.errorMessage] : [];
      var isApplied = (coupon?.couponCodes?.length ?? 0) > 0 && coupon?.errorMessage == null && response.data.discount > 0;
      var showPromo = isApplied || messages.length > 0;
      if (handleDiscountErrors || isApplied) {
        setCouponMessages(messages);
        setShowPromo(showPromo);

        if (coupon?.errorMessage) {
          setPromoCode(coupon?.couponCodes?.[0]?.couponCode ?? "");
        }
        else {
          setCouponCodes(coupon?.couponCodes ?? []);
        }
      }
      setCouponApplied(isApplied);

      if (response.data.disableCheckoutButtonText) {
        setNotification(response.data.disableCheckoutButtonText, "error", true, true);
      }
    }
  }

  // LEFT actions
  const handleRemove = async (id: number, isEagleView = false) => {
    try {
      setLoadingStatus(LoadingStatusEnum.LOADING);
      if (isEagleView) {
        clearFromStorage(userId, STORAGE_KEYS.EAGLEVIEW_ADDRESS_PREFILL);
        clearFromStorage(userId, STORAGE_KEYS.PICKUP_DELIVERY);
      }
      var response = await dataService.removeCartItem(id);
      window.dispatchEvent(new Event("refresh-cart-silent"));

      if (response.success) {
        await getCart();
        setLoadingStatus(LoadingStatusEnum.SUCCESS);
      } else {
        setNotification(response.message || "Failed to remove cart item.", "error");
        setLoadingStatus(LoadingStatusEnum.ERROR);
      }
    } catch (error) {
      setLoadingStatus(LoadingStatusEnum.ERROR);
      setNotification("An unexpected error occurred while removing item.", "error");
    }
  };

  const handleQtyChange = async (id: number, nextQty: number, unitPrice: number) => {
    if (nextQty < 1) return false;
    try {
      var response = await dataService.updateCartItemQuantity(id, nextQty);
      if (response.success) {
        await getCart();
      }
      else if (response.error || response.message) {
        setNotification(
          <>
            {response.message || response.error}
          </>,
          "error"
        );
        return false;
      }
      return true;
    } catch (error) {
      setNotification("An unexpected error occurred while changing quantity.", "error");
      return false;
    }
  };

  const handleRemoveAll = async () => {
    try {
      setLoadingStatus(LoadingStatusEnum.LOADING);
      clearFromStorage(userId, STORAGE_KEYS.EAGLEVIEW_ADDRESS_PREFILL);
      if (cartHasEagleView) {
        clearFromStorage(userId, STORAGE_KEYS.PICKUP_DELIVERY);
      }
      const response = await dataService.removeCartItems();
      window.dispatchEvent(new Event("refresh-cart-silent"));

      if (response.success) {
        await getCart();
        setLoadingStatus(LoadingStatusEnum.SUCCESS);
      } else {
        setNotification(response.message || "Failed to clear cart.", "error");
        setLoadingStatus(LoadingStatusEnum.ERROR);
      }
    } catch (error) {
      setLoadingStatus(LoadingStatusEnum.ERROR);
      setNotification("An unexpected error occurred while removing all items.", "error");
    }
  };

  // RIGHT actions
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      return;
    }

    setLoadingStatus(LoadingStatusEnum.LOADING);

    try {
      var response = await dataService.applyPromo(promoCode.trim());
      if (response.success) {
        setLoadingStatus(LoadingStatusEnum.SUCCESS);
        window.dispatchEvent(new Event("refresh-cart-silent"));
        await getCart();
      }
      else if (response.message || response.error) {
        setLoadingStatus(LoadingStatusEnum.ERROR);
        setCouponMessages([response.message ?? response.error ?? ""]);
      }
      else {
        setLoadingStatus(LoadingStatusEnum.IDLE);
        setPromoCode("");
        setShowPromo(false);
      }
    } catch (error) {
      setLoadingStatus(LoadingStatusEnum.ERROR)
      setNotification("An unexpected error occurred while applying promo code.", "error");
    }
  };

  const handleRemovePromo = async (couponCode: string) => {
    try {
      var response = await dataService.removePromo(couponCode);
      if (response.success) {
        window.dispatchEvent(new Event("refresh-cart-silent"));
        await getCart();
        setPromoCode("");
        setShowPromo(false);
      } else {
        setNotification(response.message || "Failed to remove promo code.", "error");
      }
    } catch (error) {
      setNotification("An unexpected error occurred while removing promo code.", "error");
    }
  };

  const handleCheckout = () => {
    if (!itemsCount) return;
    const modeToSave = cartHasEagleView ? "delivery" : fulfillment;
    saveToStorage(userId, STORAGE_KEYS.CART_PICKUP_DELIVERY, modeToSave);

    const cached = loadFromStorage<SavedDeliveryPickupPayload>(userId, STORAGE_KEYS.PICKUP_DELIVERY);
    if (cached && cached.isPickup && modeToSave === "delivery") {
      clearFromStorage(userId, STORAGE_KEYS.PICKUP_DELIVERY);
    }
    if (cached && !cached.isPickup && modeToSave === "pickup") {
      clearFromStorage(userId, STORAGE_KEYS.PICKUP_DELIVERY);
    }

    window.location.href = "/lbpcustomer/checkout";
  }

  if (loading) {
    return (
      <Box p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!itemsCount) {
    return (
      <Box p={3}>
        <BuilderIOSection section={BuilderIOSections.EmptyCart} />
      </Box>
    );
  }

  const addCartToTemplate = async (productListId: string, type?: ProductListEnumType) => {
    try {
      var cartItemIds = cartItemForTemplate?.id ? [cartItemForTemplate?.id] : [];
      let response;
      if (type === ProductListEnumType.QuoteList) {
        response = await dataService.AddProductToEstimate({
          estimateId: productListId,
          source: ItemSourceEnum.Cart,
          cartItemIds: cartItemIds
        });
      } else {
        response = await dataService.AddProductToTemplates({
          templateId: productListId,
          source: ItemSourceEnum.Cart,
          cartItemIds: cartItemIds
        });
      }

      const { success, data } = response || {};
      const { warnings = [], id, productListName: name, successMessage } = data || {};

      if (success && warnings.length === 0) {
        const url = `${process.env.REACT_APP_WEB_URL}/ProductListItem/List/${id}`;

        setNotification(
          <>
            {successMessage.includes("{0}") ? (
              successMessage.split("{0}").map((part, index, arr) => (
                <React.Fragment key={index}>
                  {part}
                  {index < arr.length - 1 && <a href={url}>{name}</a>}
                </React.Fragment>
              ))
            ) : (
              <>
                {successMessage} <a href={url}>{name}</a>
              </>
            )}
          </>
          ,
          "success"
        );
      } else {
        setNotification(warnings[0] ?? "Failed to add cart item.", "error");
      }
    } catch (error) {
      setNotification("An unexpected error occurred while adding the cart item.", "error");
    }
    finally {
      setSidebarOpen(false);
    }
  }

  const addCartToNewTemplate = async (name?: string, description?: string, type?: ProductListEnumType) => {
    if (!type || !name) {
      return;
    }

    try {
      const response = await dataService.createNewEstimateOrTemplate(type, name.trim(), (description ?? "").trim());
      if (response.success && (response.data.errorMessage == null || response.data.errorMessage?.trim() === "")) {
        addCartToTemplate(response.data.id?.toString() ?? "0", type);
      }
      else if (response.data.errorMessage && response.data.errorMessage.trim() !== "") {
        setNotification(response.data.errorMessage, "error");
      }
    } catch (error) {
      setNotification("An unexpected error occurred while adding the product.", "error");
    }
    finally {
      setSidebarOpen(false);
    }
  };

  return (
    <>
      <Grid
        container
        sx={{ pt: 3, px: { xs: 2, xl: 5 } }}
        spacing={5}
      >
        <Grid item xs={12} md={12} >
          <BuilderIOSection section={BuilderIOSections.CartHeader} />
        </Grid>
        {/* LEFT: Cart list */}
        <Grid item xs={12} md={7} >
          <Box
            sx={{

              bgcolor: "#f6f6f6",
              borderRadius: 2,
              p: { xs: 2, md: 3 },
            }}
          >
            {/* Title bar */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: "24px", justifyContent: "space-between" }}>
              <Typography
                component="div"
                sx={{
                  fontFamily: "Montserrat, Verdana, sans-serif",
                  fontWeight: 600,
                  fontSize: { xs: 26, md: 36 },
                  lineHeight: { xs: "32px", md: "44px" },
                  color: "#767676",
                  whiteSpace: "nowrap",
                }}
              >
                Your Cart{" "} ({itemsCount})
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  borderBottom: "1px solid #62B5E5",
                  height: 0,
                  minWidth: 70,
                  "@media (max-width:410px)": {
                    display: "none",
                  },
                }}
              />

              {!cartHasEagleView && (
                <Box
                  onClick={() => {
                    setShareOpen(true);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 1,
                    color: "#0076CE",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <ShareOutlinedIcon fontSize="small" />
                  <Typography
                    sx={{
                      fontFamily: "Montserrat",
                      fontWeight: 600,
                      fontSize: 14,
                      lineHeight: "17px",
                      color: "#003B5C",
                      ml: "8px",
                    }}
                  >
                    Share
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Header actions */}
            <Box
              sx={{
                textAlign: "end",
                color: "#003B5C",
                fontFamily: "Montserrat",
                fontWeight: 600,
                fontSize: 14,
                lineHeight: "17px",
                my: "16px",
              }}
            >
              {!cartHasEagleView && (
                <>
                  <MuiLink component="button" type="button" sx={{ color: "#003B5C", textDecoration: "none", mr: 1 }} onClick={() => setSidebarOpen(true)}>
                    Save All to Template
                  </MuiLink>
                  |
                </>
              )}
              <MuiLink
                disabled={loadingStatus === LoadingStatusEnum.LOADING}
                component="button"
                type="button"
                sx={{
                  color: loadingStatus === LoadingStatusEnum.LOADING ? "#707372" : "#003B5C",
                  textDecoration: "none",
                  ml: 1,
                  cursor: loadingStatus === LoadingStatusEnum.LOADING ? "default" : "pointer"
                }}
                onClick={handleRemoveAll}
              >
                Remove All Items
              </MuiLink>
            </Box>

            {/* Items */}
            <Box sx={{ bgcolor: "white" }}>
              {items.map((item) => (
                <CartItemRow
                  key={item.id}
                  item={item}
                  onQtyChange={(q) => handleQtyChange(item.id, q, item.unitPrice)}
                  onRemove={() => handleRemove(item.id, isEagleViewProduct(item.sku))}
                  onOpenSidebar={(o) => setSidebarOpen(o)}
                  onCartItemForTemplate={(c) => setCartItemForTemplate(c)}
                  loadingStatus={loadingStatus}
                  cartLineRiserPan={item.riserPan}
                />
              ))}
            </Box>

            <CartToProductListSidebar
              open={sidebarOpen}
              onCartToNewTemplate={(name?: string, description?: string, type?: ProductListEnumType) => {
                if (name && type) {
                  addCartToNewTemplate(name, description, type);
                  setSidebarOpen(false);
                }
              }}
              onCartToTemplate={(id?: string, type?: ProductListEnumType) => {
                if (id && type) {
                  addCartToTemplate(id, type);
                }
              }}
              onClose={() => {
                setCartItemForTemplate(null);
                setSidebarOpen(false);
              }} />
          </Box>
        </Grid>

        {/* RIGHT: Order summary */}
        <Grid item xs={12} md={5}>
          <CartCheckoutSummary
            hideCheckoutButton={cart?.hideCheckoutButton ?? false}
            disableCheckoutButton={cart?.disableCheckoutButton ?? false}
            itemsCount={itemsCount}
            subtotal={orderSubtotal}
            city={cart?.pickupPoint?.name || cart?.pickupPoint?.city}
            warehouseEmail={cart?.warehouseEmail}
            contactPhoneNumber={cart?.contactPhoneNumber}
            onCheckout={handleCheckout}
            fulfillment={fulfillment}
            onChangeFulfillment={setFulfillment}
            showPromo={showPromo}
            onTogglePromo={() => setShowPromo((s) => !s)}
            promoCode={promoCode}
            discount={cart?.discount ?? 0}
            holidays={cart?.holidays ?? []}
            onChangePromo={setPromoCode}
            onApplyPromo={handleApplyPromo}
            loadingStatus={loadingStatus}
            setLoadingStatus={setLoadingStatus}
            appliedCodes={couponCodes}
            onRemoveCode={handleRemovePromo}
            couponMessages={couponMessages}
            couponApplied={couponApplied}
            showTerms={!!(cart?.isTCChecked && cart?.termsAndConditions)}
            onOpenTerms={() => setShowTerms(true)}
            onOpenSidebar={(o) => setSidebarOpen(o)}
            cartHasEagleView={cartHasEagleView}
            eagleViewAddressAvailable={eagleViewAddressAvailable}
          />
        </Grid>

        {/* Terms & Conditions modal */}
        <Dialog open={showTerms} onClose={() => setShowTerms(false)} fullWidth maxWidth="sm">
          <DialogTitle>Terms & Conditions</DialogTitle>
          <DialogContent dividers sx={{ whiteSpace: "pre-wrap" }}>
            {cart?.termsAndConditions || "No terms provided."}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowTerms(false)} variant="contained">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Share / Email Cart modal */}
        <ShareCartDialog open={shareOpen} onClose={() => setShareOpen(false)} cart={cart} />
      </Grid>
    </>
  );
};

export default CartPage;